/** SubscriptionRequestWizard - v2026.06.129
 *  Wizard 3 etapes pour demande d offre commerciale depuis BillingPage.
 *  Step 1 : Selection partenaire (auto si tenant.partnerSlug deja defini)
 *  Step 2 : nb users, requestor name/email, message
 *  Step 3 : Recap + envoi POST /api/billing/request-offer
 *  Step 4 : Page confirmation
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog, DialogSurface, DialogBody, DialogTitle, DialogContent, DialogActions,
  Button, Input, Textarea, Field, Dropdown, Option,
  Spinner, MessageBar, MessageBarBody, MessageBarTitle,
  Body1, Caption1, Title3, Badge, tokens,
} from '@fluentui/react-components';
import {
  CheckmarkCircle24Filled, ArrowRight20Regular, ArrowLeft20Regular,
} from '@fluentui/react-icons';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../auth/AuthContext';
import { useActiveTenant } from '../../hooks/useActiveTenant';
import { onboardingApi } from '../../api/onboardingApi';

export type SubscriptionPlan = 'starter' | 'pro' | 'business' | 'enterprise';

// v2026.06.143 - L API /api/partners (ListPartnersPublic) renvoie `name`
// (et non `organizationName`). On garde un alias retrocompat au cas ou.
interface PublicPartner {
  slug:             string;
  name:             string;
  organizationName?: string;   // retrocompat
  logoUrl?:         string;
}

interface Props {
  plan:    SubscriptionPlan;
  onClose: () => void;
}

export function SubscriptionRequestWizard({ plan, onClose }: Props) {
  const { t } = useTranslation();
  const { user }   = useAuth();
  const { tenant } = useActiveTenant();

  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  const [partners, setPartners] = useState<PublicPartner[]>([]);
  const [partnersLoading, setPartnersLoading] = useState(false);
  // Le partenaire rattache au client : on le lit depuis l onboarding status
  // (tenant.partnerSlug n est pas toujours peuple), avec repli sur le tenant.
  // Le modele stocke partnerId ; partnerSlug est resolu par le backend.
  const onboardingQ = useQuery({ queryKey: ['onboarding-status'], queryFn: () => onboardingApi.status(), staleTime: 60_000, retry: false });
  const tenantPartnerSlug = (onboardingQ.data?.partnerSlug || (tenant as { partnerSlug?: string })?.partnerSlug || '');
  const tenantPartnerId   = (onboardingQ.data?.partnerId || (tenant as { partnerId?: string })?.partnerId || '');
  const tenantPartnerName = (onboardingQ.data?.partnerName || '');
  // Valeur effectivement soumise : on privilegie le slug, repli sur l id.
  const fixedPartnerValue = tenantPartnerSlug || tenantPartnerId;
  const hasFixedPartner   = Boolean(fixedPartnerValue);
  const [partnerSlug, setPartnerSlug] = useState<string>(fixedPartnerValue);
  // L onboarding peut resoudre apres le 1er rendu -> on force la valeur (verrouillee).
  useEffect(() => {
    if (fixedPartnerValue && partnerSlug !== fixedPartnerValue) setPartnerSlug(fixedPartnerValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fixedPartnerValue]);

  const [nbUsers, setNbUsers]   = useState<number>(10);
  const [reqName, setReqName]   = useState<string>(user?.name ?? user?.email?.split('@')[0] ?? '');
  const [reqEmail, setReqEmail] = useState<string>(user?.email ?? '');
  const [message, setMessage]   = useState<string>('');

  const [confirmedRequestId, setConfirmedRequestId] = useState<string | null>(null);
  const [confirmedSalesContact, setConfirmedSalesContact] = useState<string | null>(null);
  const [confirmedPartnerName, setConfirmedPartnerName]   = useState<string | null>(null);

  useEffect(() => {
    if (hasFixedPartner) return;
    setPartnersLoading(true);
    fetch('/api/partners')
      .then(r => r.ok ? r.json() : Promise.reject(r.statusText))
      .then((data: { partners?: PublicPartner[] }) => setPartners(data.partners ?? []))
      .catch(() => setPartners([]))
      .finally(() => setPartnersLoading(false));
  }, [hasFixedPartner]);

  const planLabel = useMemo(() => t(`subscriptionRequest.plans.${plan}`), [t, plan]);

  const step2Valid =
    reqName.trim().length >= 2 &&
    /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(reqEmail) &&
    nbUsers >= 1 && nbUsers <= 10000;

  async function submit() {
    setError(null);
    setSending(true);
    try {
      const res = await fetch('/api/billing/request-offer', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({
          plan,
          partnerSlug:      partnerSlug || null,
          nbUsers,
          requestorName:    reqName.trim(),
          requestorEmail:   reqEmail.trim(),
          message:          message.trim() || undefined,
          tenantId:         tenant?.id,
          organizationName: tenant?.name,
        }),
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || `HTTP ${res.status}`);
      }
      const data = await res.json() as { requestId: string; salesContact: string; partnerName?: string | null };
      setConfirmedRequestId(data.requestId);
      setConfirmedSalesContact(data.salesContact);
      setConfirmedPartnerName(data.partnerName ?? null);
      setStep(4);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSending(false);
    }
  }

  return (
    <Dialog open={true} onOpenChange={(_, d) => { if (!d.open) onClose(); }}>
      <DialogSurface style={{ maxWidth: 640 }}>
        <DialogBody>
          <DialogTitle>{t('subscriptionRequest.title', { plan: planLabel })}</DialogTitle>
          <DialogContent>
            {step < 4 && (
              <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
                {[1, 2, 3].map(s => (
                  <div key={s} style={{
                    flex: 1, height: 4, borderRadius: 2,
                    background: s <= step ? tokens.colorBrandBackground : tokens.colorNeutralBackground3,
                  }} />
                ))}
              </div>
            )}

            {error && (
              <MessageBar intent="error" style={{ marginBottom: 12 }}>
                <MessageBarBody>{error}</MessageBarBody>
              </MessageBar>
            )}

            {step === 1 && (
              <div>
                <Title3 block style={{ marginBottom: 8 }}>{t('subscriptionRequest.step1.title')}</Title3>
                <Body1 block style={{ color: tokens.colorNeutralForeground2, marginBottom: 16 }}>
                  {t('subscriptionRequest.step1.lead')}
                </Body1>

                {hasFixedPartner ? (
                  <Field
                    label={t('subscriptionRequest.step1.partnerLabel')}
                    hint={t('subscriptionRequest.step1.fixedPartnerHint', 'Partenaire rattaché à votre compte — non modifiable.')}
                  >
                    <Dropdown
                      disabled
                      value={tenantPartnerName
                        || partners.find(p => p.slug === fixedPartnerValue || p.slug === tenantPartnerSlug)?.name
                        || partners.find(p => p.slug === fixedPartnerValue || p.slug === tenantPartnerSlug)?.organizationName
                        || fixedPartnerValue}
                      selectedOptions={[fixedPartnerValue]}
                    >
                      <Option value={fixedPartnerValue}>
                        {tenantPartnerName || fixedPartnerValue}
                      </Option>
                    </Dropdown>
                  </Field>
                ) : (
                  <Field label={t('subscriptionRequest.step1.partnerLabel')} hint={t('subscriptionRequest.step1.partnerHint')}>
                    {partnersLoading ? (
                      <Spinner size="tiny" label={t('subscriptionRequest.step1.loading')} />
                    ) : (
                      <Dropdown
                        value={partnerSlug
                          ? (partners.find(p => p.slug === partnerSlug)?.name
                             ?? partners.find(p => p.slug === partnerSlug)?.organizationName
                             ?? partnerSlug)
                          : t('subscriptionRequest.step1.noPartner')}
                        selectedOptions={[partnerSlug]}
                        onOptionSelect={(_, d) => setPartnerSlug(d.optionValue ?? '')}
                      >
                        <Option value="">{t('subscriptionRequest.step1.noPartner')}</Option>
                        {partners.map(p => (
                          <Option key={p.slug} value={p.slug}>{p.name ?? p.organizationName ?? p.slug}</Option>
                        ))}
                      </Dropdown>
                    )}
                  </Field>
                )}
              </div>
            )}

            {step === 2 && (
              <div>
                <Title3 block style={{ marginBottom: 8 }}>{t('subscriptionRequest.step2.title')}</Title3>
                <Body1 block style={{ color: tokens.colorNeutralForeground2, marginBottom: 16 }}>
                  {t('subscriptionRequest.step2.lead')}
                </Body1>

                <Field label={t('subscriptionRequest.step2.nbUsersLabel')} required style={{ marginBottom: 12 }}>
                  <Input type="number" min={1} max={10000}
                    value={String(nbUsers)}
                    onChange={(_, d) => setNbUsers(parseInt(d.value, 10) || 0)} />
                </Field>
                <Field label={t('subscriptionRequest.step2.reqNameLabel')} required style={{ marginBottom: 12 }}>
                  <Input value={reqName} onChange={(_, d) => setReqName(d.value)} />
                </Field>
                <Field label={t('subscriptionRequest.step2.reqEmailLabel')} required style={{ marginBottom: 12 }}>
                  <Input type="email" value={reqEmail} onChange={(_, d) => setReqEmail(d.value)} />
                </Field>
                <Field label={t('subscriptionRequest.step2.messageLabel')} hint={t('subscriptionRequest.step2.messageHint')}>
                  <Textarea rows={3} value={message} onChange={(_, d) => setMessage(d.value)} />
                </Field>
              </div>
            )}

            {step === 3 && (
              <div>
                <Title3 block style={{ marginBottom: 8 }}>{t('subscriptionRequest.step3.title')}</Title3>
                <Body1 block style={{ color: tokens.colorNeutralForeground2, marginBottom: 16 }}>
                  {t('subscriptionRequest.step3.lead')}
                </Body1>
                <div style={{
                  background: tokens.colorNeutralBackground2,
                  border: `1px solid ${tokens.colorNeutralStroke2}`,
                  borderRadius: 8, padding: 16,
                }}>
                  <RecapRow label={t('subscriptionRequest.recap.plan')} value={
                    <Badge appearance="filled" style={{ background: tokens.colorBrandBackground, color: '#fff' }}>{planLabel}</Badge>
                  } />
                  <RecapRow label={t('subscriptionRequest.recap.nbUsers')} value={nbUsers} />
                  <RecapRow label={t('subscriptionRequest.recap.partner')} value={
                    partnerSlug
                      ? (partners.find(p => p.slug === partnerSlug)?.name
                         ?? partners.find(p => p.slug === partnerSlug)?.organizationName
                         ?? partnerSlug)
                      : t('subscriptionRequest.recap.noPartner')
                  } />
                  <RecapRow label={t('subscriptionRequest.recap.requestor')} value={`${reqName} <${reqEmail}>`} />
                  {tenant?.name && (
                    <RecapRow label={t('subscriptionRequest.recap.organization')} value={tenant.name} />
                  )}
                  {message && (
                    <RecapRow label={t('subscriptionRequest.recap.message')} value={message} />
                  )}
                </div>
              </div>
            )}

            {step === 4 && (
              <div style={{ textAlign: 'center', padding: '24px 12px' }}>
                <CheckmarkCircle24Filled primaryFill={tokens.colorPaletteGreenForeground1} style={{ width: 64, height: 64 }} />
                <Title3 block style={{ marginTop: 12, marginBottom: 8 }}>{t('subscriptionRequest.step4.title')}</Title3>
                <Body1 block style={{ color: tokens.colorNeutralForeground2, marginBottom: 12 }}>
                  {confirmedPartnerName
                    ? t('subscriptionRequest.step4.bodyPartner', { partner: confirmedPartnerName })
                    : t('subscriptionRequest.step4.bodyDirect')}
                </Body1>
                {confirmedSalesContact && (
                  <Caption1 block style={{ marginBottom: 8 }}>
                    {t('subscriptionRequest.step4.salesContact')} : <strong>{confirmedSalesContact}</strong>
                  </Caption1>
                )}
                {confirmedRequestId && (
                  <Caption1 block style={{ color: tokens.colorNeutralForeground3, fontFamily: 'monospace' }}>
                    {t('subscriptionRequest.step4.ref')} : {confirmedRequestId}
                  </Caption1>
                )}
              </div>
            )}
          </DialogContent>

          <DialogActions>
            {step < 4 && (
              <>
                {step > 1 && (
                  <Button appearance="secondary" icon={<ArrowLeft20Regular />}
                    onClick={() => setStep((step - 1) as 1 | 2 | 3)}>
                    {t('subscriptionRequest.actions.back')}
                  </Button>
                )}
                <Button appearance="subtle" onClick={onClose}>{t('subscriptionRequest.actions.cancel')}</Button>
                {step === 1 && (
                  <Button appearance="primary" iconPosition="after" icon={<ArrowRight20Regular />}
                    onClick={() => setStep(2)}>
                    {t('subscriptionRequest.actions.next')}
                  </Button>
                )}
                {step === 2 && (
                  <Button appearance="primary" iconPosition="after" icon={<ArrowRight20Regular />}
                    disabled={!step2Valid}
                    onClick={() => setStep(3)}>
                    {t('subscriptionRequest.actions.next')}
                  </Button>
                )}
                {step === 3 && (
                  <Button appearance="primary"
                    disabled={sending}
                    onClick={submit}>
                    {sending ? <Spinner size="tiny" /> : t('subscriptionRequest.actions.send')}
                  </Button>
                )}
              </>
            )}
            {step === 4 && (
              <Button appearance="primary" onClick={onClose}>
                {t('subscriptionRequest.actions.close')}
              </Button>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

function RecapRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: 12, padding: '6px 0', borderBottom: `1px solid ${tokens.colorNeutralStroke2}` }}>
      <Caption1 style={{ minWidth: 130, color: tokens.colorNeutralForeground3 }}>{label}</Caption1>
      <Body1 style={{ flex: 1, fontWeight: 600 }}>{value}</Body1>
    </div>
  );
}
